/**
 * @fileoverview Typed browser transport for the versioned leads API.
 * @provides api request helper, CSRF persistence in memory, and normalized API errors.
 * @usedBy React Router loaders/actions and application pages.
 * @uses Shared API envelope types, Fetch API, and the configured API origin.
 * @security Credentials use HttpOnly cookies; CSRF is intentionally never persisted to localStorage.
 */
import type { ApiError, ApiSuccess, SessionUser } from "@softwareseva/contracts";
export const API_ORIGIN = import.meta.env.VITE_API_ORIGIN ?? "https://api-leads.softwareseva.org";
let csrfToken = "";
export class ApiClientError extends Error { constructor(public status: number, public code: string, message: string, public requestId: string) { super(message); } }
export const setCsrfToken = (value: string) => { csrfToken = value; };

export async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_ORIGIN}/v1${path}`, { ...options, credentials: "include", headers: { "content-type": "application/json", ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}), ...options.headers } });
  const payload = await response.json() as ApiSuccess<T> | ApiError;
  if (!response.ok) { const error = payload as ApiError; throw new ApiClientError(response.status, error.code, error.message, error.requestId); }
  return (payload as ApiSuccess<T>).data;
}

export async function loadSession(): Promise<SessionUser | null> {
  try { const result = await api<{ user: SessionUser }>("/auth/session"); setCsrfToken(result.user.csrfToken); return result.user; } catch (error) { if (error instanceof ApiClientError && [401, 403].includes(error.status)) return null; throw error; }
}
